var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import Webcam from 'react-webcam';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import { FiCheck, FiX } from 'react-icons/fi';
import Loading from '../Loading';
import LivenessIntro from './LivenessIntro';
import LivenessCameraChrome from './LivenessCameraChrome';
import TopNavigation from '../TopNavigation';
import CameraError from '../CameraError';
import Service from '../../services/Service';
import { useTranslation } from '../../i18n';
import { isDebugFlagEnabled } from '../../util/debug-flags.util';
import * as FormActions from '../../store/form/actions';
import * as LoaderActions from '../../store/loader/actions';
import { Container, DebugPanel, DebugBadge, InfoContainer, InfoTitle, ResultIcon, ResultMessage, PrimaryButton, InlineSpinner, } from './styles';
// Rótulos por gesto. As chaves batem com as constantes do LocalLivenessService no backend.
var gestureInstructionKey = {
    FRONTAL: 'livenessLocal.gestureFrontal',
    TURN_LEFT: 'livenessLocal.gestureTurnLeft',
    TURN_RIGHT: 'livenessLocal.gestureTurnRight',
    BLINK: 'livenessLocal.gestureBlink',
    SMILE: 'livenessLocal.gestureSmile',
};
var videoConstraints = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: 'user',
};
/** Segundos de preparo antes de capturar o frame do gesto. */
var GESTURE_COUNTDOWN_SECONDS = 3;
/** Tentativas por gesto antes de reiniciar o desafio (evita reenvio infinito). */
var MAX_ATTEMPTS_PER_GESTURE = 3;
/** Sequência usada apenas no modo simulado (?livenessMock=1). */
var MOCK_SEQUENCE = ['FRONTAL', 'TURN_LEFT', 'TURN_RIGHT', 'BLINK', 'SMILE'];
/**
 * Extrai a sequência de gestos tolerando variações de formato do backend:
 * array JSON (["FRONTAL","SMILE"]), lista separada por vírgula/;/| ou array puro.
 */
function parseChallengeSequence(raw) {
    if (Array.isArray(raw)) {
        return raw.map(function (item) { return String(item).trim(); }).filter(Boolean);
    }
    if (typeof raw !== 'string') {
        return [];
    }
    var value = raw.trim();
    if (!value) {
        return [];
    }
    if (value.charAt(0) === '[') {
        try {
            var parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed.map(function (item) { return String(item).trim(); }).filter(Boolean);
            }
        }
        catch (err) {
            // Não é JSON válido: cai para a separação por delimitadores abaixo.
        }
    }
    return value
        .split(/[,;|]/)
        .map(function (item) { return item.trim(); })
        .filter(Boolean);
}
/**
 * Tenta recuperar a mensagem real devolvida pelo backend.
 *
 * O interceptor do Api rejeita com `error.response`, então o corpo fica em
 * `err.data`. Em falha de rede não há response e nada disso existe — por isso
 * todos os acessos são defensivos.
 */
function extractBackendMessage(err) {
    var _a, _b;
    var data = (_a = err === null || err === void 0 ? void 0 : err.data) !== null && _a !== void 0 ? _a : (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data;
    var message = (data === null || data === void 0 ? void 0 : data.title) || (data === null || data === void 0 ? void 0 : data.message) || (data === null || data === void 0 ? void 0 : data.error);
    return typeof message === 'string' && message.trim() ? message : null;
}
var StepLivenessLocal = function (_a) {
    var step = _a.step;
    var _b = useSelector(function (state) { return state.form; }), form = _b.form, formInfo = _b.formInfo;
    var t = useTranslation().t;
    var dispatch = useDispatch();
    var navigate = useNavigate();
    /**
     * Fases da tela:
     *  - 'intro'     tutorial, antes de ligar a camera (nao pedimos camera sem o
     *                usuario aceitar comecar);
     *  - 'challenge' camera ligada, conduzindo os gestos;
     *  - 'result'    feedback final de sucesso ou falha.
     */
    var _c = React.useState('intro'), phase = _c[0], setPhase = _c[1];
    var _d = React.useState(null), result = _d[0], setResult = _d[1];
    var _e = React.useState(false), starting = _e[0], setStarting = _e[1];
    var _f = React.useState(false), awaitingResponse = _f[0], setAwaitingResponse = _f[1];
    var _g = React.useState(false), started = _g[0], setStarted = _g[1];
    var _h = React.useState([]), sequence = _h[0], setSequence = _h[1];
    var _j = React.useState(0), currentGesture = _j[0], setCurrentGesture = _j[1];
    var _k = React.useState(''), statusMessage = _k[0], setStatusMessage = _k[1];
    var _l = React.useState(0), countdown = _l[0], setCountdown = _l[1];
    // Estado da câmera: sem isso, uma falha de permissão deixava a tela preta em silêncio.
    var _m = React.useState(false), cameraReady = _m[0], setCameraReady = _m[1];
    var _o = React.useState(true), cameraPermission = _o[0], setCameraPermission = _o[1];
    var _p = React.useState(false), cameraNotFound = _p[0], setCameraNotFound = _p[1];
    var _q = React.useState(false), retryPermission = _q[0], setRetryPermission = _q[1];
    var webcamRef = React.useRef(null);
    var attemptsRef = React.useRef(0);
    // Evita emitir o desafio mais de uma vez quando a camera fica pronta.
    var challengeRequestedRef = React.useRef(false);
    // Recursos de depuração: só ligam por query param e nunca em produção.
    var location = useLocation();
    var debugEnabled = React.useMemo(function () { return isDebugFlagEnabled(location.search, 'livenessDebug'); }, [location.search]);
    var mockEnabled = React.useMemo(function () { return isDebugFlagEnabled(location.search, 'livenessMock'); }, [location.search]);
    var _r = React.useState([]), debugLog = _r[0], setDebugLog = _r[1];
    /** Registra uma linha no painel de diagnóstico (e no console). */
    var logDebug = React.useCallback(function (line) {
        var stamped = "".concat(new Date().toLocaleTimeString(), " ").concat(line);
        console.log('[liveness-local]', line);
        setDebugLog(function (previous) { return __spreadArray(__spreadArray([], previous.slice(-40), true), [stamped], false); });
    }, []);
    // O backend recebe o número do step; no JSON do form ele costuma vir como string ("1").
    var stepNumber = Number(form.steps[step].step);
    var tokenOnboarding = (formInfo === null || formInfo === void 0 ? void 0 : formInfo.tokenOnboarding) || '';
    var busy = starting || awaitingResponse;
    var capture = function () {
        var instance = webcamRef.current;
        if (!instance) {
            return null;
        }
        // Só captura com o vídeo efetivamente pronto, senão vem frame preto/nulo.
        var video = instance.video;
        if (!video || video.readyState < 2 || !video.videoWidth) {
            return null;
        }
        return instance.getScreenshot();
    };
    var resetChallenge = function () {
        attemptsRef.current = 0;
        challengeRequestedRef.current = false;
        setStarted(false);
        setSequence([]);
        setCurrentGesture(0);
        setStatusMessage('');
        setCountdown(0);
        setStarting(false);
        setAwaitingResponse(false);
    };
    /** Encerra o desafio mostrando o feedback final ao usuario. */
    var showResult = function (ok, message) {
        resetChallenge();
        setResult({ ok: ok, message: message });
        setPhase('result');
    };
    /** Volta ao tutorial para uma nova tentativa. */
    var restartFromIntro = function () {
        resetChallenge();
        setResult(null);
        setCameraReady(false);
        setPhase('intro');
    };
    /** Fase 1: pede ao backend a emissão do desafio. */
    var startChallenge = function () {
        if (!cameraReady) {
            toast.error(t('livenessLocal.cameraNotReady'));
            return;
        }
        setStarting(true);
        setStatusMessage(t('livenessLocal.starting'));
        // Modo simulado: emite o desafio localmente para validar toda a UI dos
        // gestos sem depender do backend. Nunca conclui o step (ver finalize).
        if (mockEnabled) {
            logDebug("SIMULACAO: desafio local [".concat(MOCK_SEQUENCE.join(', '), "]"));
            setSequence(MOCK_SEQUENCE);
            setCurrentGesture(0);
            setStarted(true);
            setStatusMessage('');
            setStarting(false);
            return;
        }
        logDebug("POST /service-field step=".concat(stepNumber, " field=frame_liveness (sem value)"));
        Service.serviceField({ tokenOnboarding: tokenOnboarding, step: stepNumber, field: 'frame_liveness' })
            .then(function (response) {
            var _a;
            logDebug("resposta: ".concat(Array.isArray(response) ? "".concat(response.length, " campo(s)") : typeof response));
            // Um 200 com lista vazia significa que o backend aceitou a chamada mas
            // nenhum serviço produziu campos: normalmente o alias do serviço ou o
            // campo não estão configurados para este step. É um caso bem diferente
            // de erro de rede, então recebe mensagem e log próprios.
            if (!response || !response.length) {
                console.error('[liveness-local] Backend respondeu 200 com lista vazia — o desafio não foi emitido. ' +
                    'Verifique no produto se o step tem services=["SERVICE_LIVENESS_REACT"] e o campo "frame_liveness".', { step: stepNumber, field: 'frame_liveness', response: response });
                logDebug('FALHA: 200 com lista vazia — desafio nao emitido pelo backend');
                toast.error(t('livenessLocal.challengeNotIssued'));
                setStarting(false);
                setStatusMessage('');
                return;
            }
            var rawField = (_a = response.find(function (item) { return item.field === 'liveness_challenge_sequence'; })) !== null && _a !== void 0 ? _a : 
            // Tolera o backend nomear o campo de outra forma, desde que fale de "challenge".
            response.find(function (item) { var _a; return ((_a = item.field) === null || _a === void 0 ? void 0 : _a.indexOf('challenge')) !== -1; });
            var parsed = parseChallengeSequence(rawField === null || rawField === void 0 ? void 0 : rawField.value);
            if (!parsed.length) {
                console.error('[liveness-local] Resposta sem sequência de gestos utilizável.', {
                    step: stepNumber,
                    campos: response.map(function (item) { return item.field; }),
                    response: response,
                });
                logDebug("FALHA: sem sequencia utilizavel. campos=[".concat(response.map(function (i) { return i.field; }).join(', '), "]"));
                toast.error(t('livenessLocal.startFailed'));
                setStarting(false);
                setStatusMessage('');
                return;
            }
            logDebug("desafio recebido: [".concat(parsed.join(', '), "]"));
            setSequence(parsed);
            setCurrentGesture(0);
            setStarted(true);
            setStatusMessage('');
            setStarting(false);
        })
            .catch(function (err) {
            console.error('Liveness local: falha ao iniciar o desafio.', err);
            logDebug("ERRO na chamada: ".concat(extractBackendMessage(err) || 'sem resposta do servidor (rede/CORS/timeout)'));
            toast.error(extractBackendMessage(err) || t('livenessLocal.startFailed'));
            setStarting(false);
            setStatusMessage('');
        });
    };
    var finalize = function (fields) {
        var stepParams = {
            onboardingForm: {
                countSteps: form.countSteps,
                steps: [
                    __assign(__assign({}, form.steps[step]), { fields: fields, fieldsComplete: ['is_live_person', 'live_probability', 'liveness_challenge_sequence'] }),
                ],
            },
            tokenOnboarding: tokenOnboarding,
        };
        dispatch(LoaderActions.show(true));
        Service.saveStep(stepParams)
            .then(function (response) {
            var _a, _b;
            dispatch(LoaderActions.show(false));
            setAwaitingResponse(false);
            // O FormPage resolve a rota por ID do step (findIndex(s => s.step === state.step)),
            // NAO por indice do array. O saveStep devolve o proximo step, e é o `step`
            // dele que precisa ir no state — mesmo padrão do StepImage. Usar
            // `step + 1` (indice) nunca casa com o id (que vem como string) e deixava
            // o usuário preso na tela de sucesso.
            var nextStep = (_b = (_a = response === null || response === void 0 ? void 0 : response.steps) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.step;
            if (!response || nextStep === undefined || nextStep === null) {
                console.error('Liveness local: saveStep não retornou o próximo step.', response);
                logDebug('FALHA: saveStep sem proximo step; nao ha para onde navegar');
                showResult(false, t('livenessLocal.saveFailed'));
                return;
            }
            dispatch(FormActions.addFormStep(response));
            logDebug("saveStep ok; navegando para o step ".concat(nextStep));
            navigate('/form', { state: { step: nextStep } });
        })
            .catch(function (err) {
            dispatch(LoaderActions.show(false));
            setAwaitingResponse(false);
            console.error('Liveness local: falha ao salvar o resultado.', err);
            // O liveness passou, mas não conseguimos gravar: mostra falha para o
            // usuário poder tentar de novo, em vez de travar numa tela de sucesso.
            showResult(false, extractBackendMessage(err) || t('livenessLocal.saveFailed'));
        });
    };
    /** Fase 2/3: captura o frame do gesto atual e envia. Avança, ou finaliza no último. */
    var sendCurrentGesture = function () {
        var frame = capture();
        if (!frame) {
            toast.error(t('livenessLocal.cameraError'));
            setStatusMessage('');
            return;
        }
        // Modo simulado: avança a UI localmente, sem rede. Ao final NÃO chama
        // saveStep — quem aprova a prova de vida é o servidor, sempre. Isso impede
        // que a simulação seja usada para burlar o liveness.
        if (mockEnabled) {
            var next = currentGesture + 1;
            logDebug("SIMULACAO: gesto ".concat(currentGesture + 1, "/").concat(sequence.length, " (").concat(sequence[currentGesture], ") capturado, ").concat(frame.length, " bytes"));
            if (next < sequence.length) {
                setStatusMessage(t('livenessLocal.gestureOk'));
                setCurrentGesture(next);
                return;
            }
            logDebug('SIMULACAO: sequencia concluida. Nao envia saveStep (aprovacao e do servidor).');
            // Passa pela tela de sucesso para validar o feedback, mas sem concluir o
            // step: a aprovação continua sendo exclusivamente do servidor.
            showResult(true, 'Simulação concluída: UI validada. O step não foi concluído de propósito.');
            return;
        }
        setAwaitingResponse(true);
        setStatusMessage(t('livenessLocal.checking'));
        logDebug("POST /service-field step=".concat(stepNumber, " gesto=").concat(sequence[currentGesture], " frame=").concat(frame.length, " bytes"));
        Service.serviceField({ tokenOnboarding: tokenOnboarding, step: stepNumber, field: 'frame_liveness', value: frame })
            .then(function (response) {
            var _a;
            var isLive = (_a = response === null || response === void 0 ? void 0 : response.find(function (item) { return item.field === 'is_live_person'; })) === null || _a === void 0 ? void 0 : _a.value;
            // O backend só devolve is_live_person quando a sequência inteira foi concluída.
            if (isLive === 'true') {
                // Mostra o sucesso na hora; o saveStep continua em segundo plano e
                // a navegação acontece quando ele responde.
                setResult({ ok: true, message: t('livenessLocal.successMessage') });
                setPhase('result');
                setStarted(false);
                setCountdown(0);
                finalize(response);
                return;
            }
            if (isLive === 'false') {
                showResult(false, t('livenessLocal.notApproved'));
                return;
            }
            // Gesto aceito mas ainda faltam passos: avança para o próximo.
            setAwaitingResponse(false);
            attemptsRef.current = 0;
            var next = currentGesture + 1;
            if (next < sequence.length) {
                setStatusMessage(t('livenessLocal.gestureOk'));
                setCurrentGesture(next);
                return;
            }
            // A sequência acabou no cliente sem o backend confirmar: repete para sincronizar.
            setStatusMessage(t('livenessLocal.retry'));
        })
            .catch(function (err) {
            // Gesto não reconhecido: permite repetir o mesmo passo dentro da validade do desafio.
            console.error('Liveness local: gesto não aceito.', err);
            setAwaitingResponse(false);
            // Sem este limite o timer reenviaria indefinidamente, martelando o backend.
            attemptsRef.current += 1;
            if (attemptsRef.current >= MAX_ATTEMPTS_PER_GESTURE) {
                showResult(false, extractBackendMessage(err) || t('livenessLocal.notApproved'));
                return;
            }
            setStatusMessage(extractBackendMessage(err) || t('livenessLocal.gestureRetry'));
        });
    };
    // Mantém sempre a versão mais recente da função, para o timer nunca usar
    // estado obsoleto (o bug da versão anterior era exatamente esse).
    var sendCurrentGestureRef = React.useRef(sendCurrentGesture);
    React.useEffect(function () {
        sendCurrentGestureRef.current = sendCurrentGesture;
    });
    // Contagem regressiva antes de capturar cada gesto: dá tempo de ler a
    // instrução e se posicionar, em vez de capturar de surpresa.
    React.useEffect(function () {
        if (phase !== 'challenge' || !started || awaitingResponse) {
            return undefined;
        }
        setCountdown(GESTURE_COUNTDOWN_SECONDS);
        var remaining = GESTURE_COUNTDOWN_SECONDS;
        var intervalId = window.setInterval(function () {
            remaining -= 1;
            setCountdown(remaining > 0 ? remaining : 0);
            if (remaining <= 0) {
                window.clearInterval(intervalId);
                sendCurrentGestureRef.current();
            }
        }, 1000);
        return function () { return window.clearInterval(intervalId); };
    }, [phase, started, currentGesture, awaitingResponse]);
    /**
     * Depois do tutorial, assim que a câmera estiver pronta, emite o desafio
     * sozinho. Evita um segundo clique só para "iniciar" com a câmera já aberta.
     */
    React.useEffect(function () {
        if (phase !== 'challenge' || !cameraReady || started || starting || challengeRequestedRef.current) {
            return;
        }
        challengeRequestedRef.current = true;
        startChallenge();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, cameraReady, started, starting]);
    var handleUserMedia = function (stream) {
        var _a, _b, _c, _d;
        // Safari may grant the stream before the video has a usable frame.
        var video = (_a = webcamRef.current) === null || _a === void 0 ? void 0 : _a.video;
        setCameraReady(Boolean(video && video.readyState >= 2 && video.videoWidth));
        setCameraPermission(true);
        setCameraNotFound(false);
        setRetryPermission(false);
        // As dimensões vêm do track: no onUserMedia o <video> ainda não carregou os
        // metadados, então video.videoWidth seria 0 aqui.
        var settings = (_d = (_c = (_b = stream === null || stream === void 0 ? void 0 : stream.getVideoTracks) === null || _b === void 0 ? void 0 : _b.call(stream)[0]) === null || _c === void 0 ? void 0 : _c.getSettings) === null || _d === void 0 ? void 0 : _d.call(_c);
        logDebug("camera pronta: ".concat((settings === null || settings === void 0 ? void 0 : settings.width) || '?', "x").concat((settings === null || settings === void 0 ? void 0 : settings.height) || '?', " | ") +
            "viewport ".concat(window.innerWidth, "x").concat(window.innerHeight, " | dpr ").concat(window.devicePixelRatio));
    };
    var handleUserMediaError = function (error) {
        // Antes isso não existia: qualquer falha deixava o retângulo preto sem explicação.
        console.error('Liveness local: erro ao abrir a câmera.', error);
        setCameraReady(false);
        var name = typeof error === 'string' ? error : error === null || error === void 0 ? void 0 : error.name;
        logDebug("ERRO de camera: ".concat(name || 'desconhecido'));
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            setCameraNotFound(true);
            return;
        }
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
            setCameraPermission(false);
            return;
        }
        toast.error(t('camera.accessError', { message: typeof error === 'string' ? error : (error === null || error === void 0 ? void 0 : error.message) || '' }));
    };
    var gestureLabel = function (index) {
        var gesture = sequence[index];
        if (!gesture) {
            return '';
        }
        var key = gestureInstructionKey[gesture];
        return key ? t(key) : gesture;
    };
    if (cameraNotFound || !cameraPermission) {
        return (React.createElement(CameraError, { retrying: retryPermission, cameraNotFound: cameraNotFound, setRetrying: setRetryPermission, cameraPermission: cameraPermission, setCameraPermission: setCameraPermission }));
    }
    var topNavigation = (React.createElement(TopNavigation, { title: form.steps[step].showStepName ? form.steps[step].stepName : '', showBackButton: form.steps[step].goBack, showLogo: true, headerTransparent: form.steps[step].headerTransparent }));
    var debugArea = (React.createElement(React.Fragment, null,
        mockEnabled && React.createElement(DebugBadge, null, "MODO SIMULADO \u2014 n\u00E3o conclui o step"),
        debugEnabled && (React.createElement(DebugPanel, null,
            React.createElement("strong", null,
                "liveness debug | step ",
                stepNumber,
                " | fase ",
                phase,
                " | camera ",
                cameraReady ? 'ok' : 'off',
                mockEnabled ? ' | mock' : ''),
            debugLog.length ? debugLog.join('\n') : 'aguardando eventos...'))));
    // ----------------------------------------------------------------- tutorial
    if (phase === 'intro') {
        return React.createElement(LivenessIntro, { step: step, onStart: function () { return setPhase('challenge'); }, mock: mockEnabled, debugArea: debugArea });
    }
    // ------------------------------------------------------------------ resultado
    if (phase === 'result' && result) {
        return (React.createElement(Container, null,
            React.createElement(ToastContainer, null),
            topNavigation,
            React.createElement(InfoContainer, null,
                React.createElement(ResultIcon, { "$ok": result.ok }, result.ok ? React.createElement(FiCheck, null) : React.createElement(FiX, null)),
                React.createElement(InfoTitle, null, result.ok ? t('livenessLocal.successTitle') : t('livenessLocal.failureTitle')),
                React.createElement(ResultMessage, null, result.message),
                result.ok ? (React.createElement(InlineSpinner, null,
                    React.createElement(Loading, { type: "bubbles" }))) : (React.createElement(PrimaryButton, { text: t('common.tryAgain'), onClick: restartFromIntro })),
                debugArea)));
    }
    return (React.createElement(LivenessCameraChrome, { step: step, sequence: sequence, currentGesture: currentGesture, title: started ? gestureLabel(currentGesture) : t('livenessLocal.introTitle'), countdown: countdown, busy: busy, status: statusMessage || (!cameraReady ? t('livenessLocal.cameraStarting') : ''), mock: mockEnabled, debugArea: debugArea },
        React.createElement(Webcam, { mirrored: true, audio: false, muted: true, playsInline: true, ref: webcamRef, screenshotQuality: 0.98, forceScreenshotSourceSize: true, screenshotFormat: "image/jpeg", videoConstraints: videoConstraints, onUserMedia: handleUserMedia, onLoadedData: function () { return setCameraReady(true); }, onUserMediaError: handleUserMediaError })));
};
export default StepLivenessLocal;
